import { useState } from 'react';
import { Box, Alert } from '@mui/material';
import { useConfirmationModalContext } from 'hooks/useConfirmationModal';
import { useNotificationsSettings } from './hooks/useNotificationsSettings';
import NotificationGrid from './components/NotificationGrid';
import NotificationForm from './components/NotificationForm';
import { Notification, NotificationFormData } from './types';

const NotificationSettings = () => {
    const {
        notifications,
        loading,
        showDeleted,
        setShowDeleted,
        toggleNotification,
        deleteNotification,
        restoreNotification,
        createNotification,
        updateNotification,
        toggling,
        restoring
    } = useNotificationsSettings();

    const modal = useConfirmationModalContext();

    const [selectedNotification, setSelectedNotification] = useState<Notification | null>(null);
    const [showForm, setShowForm] = useState(false);

    const handleEdit = (notification: Notification) => {
        setSelectedNotification(notification);
        setShowForm(true);
    };

    const handleDelete = async (id: string) => {
        const notification = notifications.find((n) => n.id === id);

        await modal.showConfirmation({
            title: 'Are you sure you want to proceed with this action?',
            titleSx: { fontSize: '20px', fontWeight: 600 },
            content: (
                <Alert
                    variant="filled"
                    icon={false}
                    sx={{
                        bgcolor: '#FB9000',
                        color: 'white',
                        borderRadius: '8px !important',
                        fontSize: '14px',
                        width: 'fit-content',
                        padding: '0 16px !important',
                        fontWeight: 500
                    }}
                >
                    You can restore this notification later from the &quot;Show Deleted&quot; section.
                </Alert>
            ),
            loadingText: 'Deleting...',
            forwardButtonText: 'Proceed',
            actionButtonText: 'Go back',
            onForward: async () => {
                try {
                    await deleteNotification(id);
                } catch (error: any) {
                    console.error('Error deleting notification:', error);
                }
            },
            onAction: null,
            showCheckbox: true,
            sectionTitle: 'notifications',
            recordType: notification?.recordType.name || '',
            actionType: 'delete'
        });
    };

    const handleRestore = async (id: string) => {
        try {
            await restoreNotification(id);
        } catch (error: any) {
            console.error('Error restoring notification:', error.message);
        }
    };

    const handleToggle = async (id: string, enabled: boolean) => {
        try {
            await toggleNotification(id, enabled);
        } catch (error: any) {
            console.error('Error toggling notification:', error.message);
        }
    };

    const handleCreate = () => {
        setSelectedNotification(null);
        setShowForm(true);
    };

    const handleCloseForm = () => {
        setShowForm(false);
        setSelectedNotification(null);
    };

    const handleSave = async (data: NotificationFormData) => {
        if (selectedNotification) {
            await updateNotification(selectedNotification.id, data);
        } else {
            await createNotification(data);
        }
    };

    if (showForm) {
        return (
            <NotificationForm
                data-testid="notification-form"
                notification={selectedNotification}
                onClose={handleCloseForm}
                onSave={handleSave}
            />
        );
    }

    return (
        <Box
            data-testid="notification-settings-container"
            sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}
        >
            <Box sx={{ flex: 1, overflow: 'hidden' }}>
                <NotificationGrid
                    notifications={notifications}
                    loading={loading}
                    showDeleted={showDeleted}
                    setShowDeleted={setShowDeleted}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    onRestore={handleRestore}
                    onToggle={handleToggle}
                    onCreate={handleCreate}
                    toggling={toggling}
                    restoring={restoring}
                />
            </Box>
        </Box>
    );
};

export default NotificationSettings;
